// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Detection guide page: family/detector icon mapping, search + family
// filter state, article + index HTML rendering,
// and the JSON loader. The four mutable state pieces
// (detectionGuideCache, detectionGuidePromise, detectionGuideSearchQuery,
// detectionGuideFamilyFilter) and the docs path live in app.js, so this
// is a factory.

export function createDetectionGuide({
  escapeHtml,
  detectionGuidePath,
  getDetectionGuideCache,
  setDetectionGuideCache,
  getDetectionGuidePromise,
  setDetectionGuidePromise,
  getDetectionGuideSearchQuery,
  setDetectionGuideSearchQuery,
  getDetectionGuideFamilyFilter,
  setDetectionGuideFamilyFilter,
}) {
  function detectionGuideList(items = []) {
    return items.length
      ? `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`
      : '<p class="detection-guide-muted">Not specified.</p>';
  }

  function detectionGuideFamilySlug(family = "") {
    return String(family)
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function detectionGuideFamilyIcon(family = "") {
    const slug = detectionGuideFamilySlug(family);
    const iconMap = {
      "data-quality": "data-quality",
      "engine-systems": "engine-systems",
      "cylinder-review-cues": "cylinder-review-cues",
      "power-and-induction": "power-and-induction",
      ignition: "ignition",
      "thermal-management": "thermal-management",
      electrical: "electrical",
      "multi-engine-comparison": "multi-engine-comparison",
    };
    return `assets/icons/${iconMap[slug] ?? "data-quality"}.png`;
  }

  function detectionGuideDetectorIcon(detector) {
    const detectorIcons = {
      turbo: "assets/icons/turbocharger.png",
      valve: "assets/icons/egt-rhythm.png",
      fuelFlowResponse: "assets/icons/fuel-flow-response.png",
      fuelPressure: "assets/icons/fuel-flow-response.png",
      propellerRpm: "assets/icons/fuel-flow-response.png",
      fuelFlowWorkload: "assets/icons/fuel-flow-response.png",
      cylinderHarmony: "assets/icons/cylinder-review-cues.png",
      cylinderPersonality: "assets/icons/cylinder-review-cues.png",
      cylinderStartupSymmetry: "assets/icons/cylinder-startup-symmetry.png",
      flightPhase: "assets/icons/flight-phase.png",
      sensorCoherence: "assets/icons/sensor-coherence.png",
      startupBehavior: "assets/icons/startup-behavior.png",
      instrumentationSuspicion: "assets/icons/instrumentation-suspicion.png",
    };
    return detectorIcons[detector?.key] ?? detectionGuideFamilyIcon(detector?.family);
  }

  function detectionGuideSearchText(detector) {
    return [
      detector.key,
      detector.slug,
      detector.label,
      detector.family,
      detector.summary,
      ...(detector.requiredData ?? []),
      ...(detector.reviewedSignals ?? []),
      ...(detector.currentTrigger ?? []),
      ...(detector.howToInspect ?? []),
      ...(detector.doesNotMean ?? []),
    ].filter(Boolean).join(" ").toLowerCase();
  }

  function detectionGuideVisibleDetectors(detectors = []) {
    const query = getDetectionGuideSearchQuery().trim().toLowerCase();
    const familyFilter = getDetectionGuideFamilyFilter();
    return detectors.filter((detector) => {
      const familyMatches = familyFilter === "all" || detectionGuideFamilySlug(detector.family) === familyFilter;
      const queryMatches = !query || detectionGuideSearchText(detector).includes(query);
      return familyMatches && queryMatches;
    });
  }

  function detectionGuideActiveSlug(detectors = []) {
    const hashSlug = decodeURIComponent(window.location.hash || "").replace(/^#/, "");
    if (hashSlug && detectors.some((detector) => detector.slug === hashSlug)) return hashSlug;
    return detectors[0]?.slug ?? "";
  }

  function detectionGuideThresholdSummary(thresholds = null) {
    if (!thresholds || typeof thresholds !== "object") return "";
    const rows = Object.entries(thresholds).map(([key, value]) => `
      <span class="detection-threshold-pill">
        <span>${escapeHtml(detectionGuideThresholdLabel(key))}</span>
        <strong>${escapeHtml(detectionGuideThresholdValue(value))}</strong>
      </span>
    `).join("");
    return rows ? `<div class="detection-threshold-row"><strong>Thresholds</strong>${rows}</div>` : "";
  }

  function detectionGuideDateLabel(value = "") {
    const [year, month, day] = String(value).split("-").map((part) => Number.parseInt(part, 10));
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      return new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric", year: "numeric" }).format(new Date(year, month - 1, day));
    }
    return String(value);
  }

  function detectionGuideFamilyDisplayLabel(family = "") {
    if (family === "Power and induction") return "Power/Induction";
    if (family === "Thermal management") return "Thermal";
    if (family === "Cylinder review cues") return "Cylinder review";
    return family;
  }

  function detectionGuideRenderControls(guide, detectors = []) {
    const meta = document.querySelector("#detectionGuideMeta");
    const search = document.querySelector("#detectionGuideSearch");
    const filters = document.querySelector("#detectionGuideFamilyFilters");
    if (meta) {
      const updated = guide?.updated ? `<span>Updated: ${escapeHtml(detectionGuideDateLabel(guide.updated))}</span>` : "";
      const version = guide?.schemaVersion ? `<span class="detection-guide-meta-chip">Schema v${escapeHtml(guide.schemaVersion)}</span>` : "";
      meta.innerHTML = [version, updated].filter(Boolean).join("");
    }
    if (search && !search.dataset.bound) {
      search.dataset.bound = "true";
      search.addEventListener("input", (event) => {
        setDetectionGuideSearchQuery(event.target.value);
        renderDetectionGuide(getDetectionGuideCache());
      });
    }
    if (search && search.value !== getDetectionGuideSearchQuery()) {
      search.value = getDetectionGuideSearchQuery();
    }
    if (!filters) return;
    const families = [...new Set(detectors.map((detector) => detector.family).filter(Boolean))];
    const currentFamilyFilter = getDetectionGuideFamilyFilter();
    const familyButtons = [
      { slug: "all", label: "All", count: detectors.length },
      ...families.map((family) => ({
        slug: detectionGuideFamilySlug(family),
        label: detectionGuideFamilyDisplayLabel(family),
        count: detectors.filter((detector) => detector.family === family).length,
      })),
    ];
    filters.innerHTML = familyButtons.map((item) => `
      <button class="${item.slug === currentFamilyFilter ? "is-active" : ""}" type="button" data-family="${escapeHtml(item.slug)}">
        <span>${escapeHtml(item.label)}</span>
        <strong>${escapeHtml(item.count)}</strong>
      </button>
    `).join("");
    filters.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", () => {
        setDetectionGuideFamilyFilter(button.dataset.family || "all");
        renderDetectionGuide(getDetectionGuideCache());
      });
    });
  }

  function detectionGuideThresholdValue(value) {
    if (Array.isArray(value)) return value.join(" - ");
    if (typeof value === "number") return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
    return String(value ?? "");
  }

  function detectionGuideThresholdLabel(key) {
    return String(key ?? "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/\bFpm\b/g, "F per minute")
      .replace(/\bF\b/g, "F")
      .replace(/\bCpm\b/g, "cycles/min")
      .replace(/\bEgt\b/g, "EGT")
      .replace(/\bCht\b/g, "CHT")
      .replace(/\bRpm\b/g, "RPM")
      .replace(/\bMap\b/g, "MAP")
      .replace(/\bGph\b/g, "GPH")
      .replace(/\b([a-z])/g, (letter) => letter.toUpperCase());
  }

  function detectionGuideThresholds(thresholds = null) {
    if (!thresholds || typeof thresholds !== "object") return "";
    const rows = Object.entries(thresholds).map(([key, value]) => `
      <div class="detection-threshold">
        <span>${escapeHtml(detectionGuideThresholdLabel(key))}</span>
        <strong>${escapeHtml(detectionGuideThresholdValue(value))}</strong>
      </div>
    `).join("");
    return rows ? `<div class="detection-threshold-grid">${rows}</div>` : "";
  }

  function detectionGuideArticle(detector) {
    return `
      <article class="detection-guide-card" id="${escapeHtml(detector.slug)}">
        <header class="detection-guide-card-header">
          <div class="detection-guide-card-icon" aria-hidden="true">
            <img src="${escapeHtml(detectionGuideDetectorIcon(detector))}" alt="">
          </div>
          <div>
            <span>${escapeHtml(detector.family)}</span>
            <h2>${escapeHtml(detector.label)}</h2>
            <p>${escapeHtml(detector.summary)}</p>
          </div>
        </header>
        <div class="detection-guide-section-grid">
          <div class="detection-guide-section">
            <h3>Required Data</h3>
            ${detectionGuideList(detector.requiredData)}
          </div>
          <div class="detection-guide-section">
            <h3>Reviewed Signals</h3>
            ${detectionGuideList(detector.reviewedSignals)}
          </div>
          <div class="detection-guide-section">
            <h3>Current Trigger</h3>
            ${detectionGuideList(detector.currentTrigger)}
          </div>
          <div class="detection-guide-section">
            <h3>How To Inspect</h3>
            ${detectionGuideList(detector.howToInspect)}
          </div>
          <div class="detection-guide-section">
            <h3>What It Does Not Mean</h3>
            ${detectionGuideList(detector.doesNotMean)}
          </div>
        </div>
      </article>
    `;
  }

  function renderDetectionGuide(guide) {
    const detectors = Array.isArray(guide?.detectors) ? guide.detectors : [];
    const content = document.querySelector("#detectionGuideContent");
    const index = document.querySelector("#detectionGuideIndex");
    if (!content || !index) return;
    detectionGuideRenderControls(guide, detectors);
    const visibleDetectors = detectionGuideVisibleDetectors(detectors);
    const activeSlug = detectionGuideActiveSlug(visibleDetectors);
    const families = [...new Set(detectors.map((detector) => detector.family).filter(Boolean))];
    index.innerHTML = families.map((family) => {
      const familyDetectors = visibleDetectors.filter((detector) => detector.family === family);
      if (!familyDetectors.length) return "";
      const familySlug = detectionGuideFamilySlug(family);
      const links = familyDetectors
        .map((detector) => `<a class="${detector.slug === activeSlug ? "is-active" : ""}" href="#${escapeHtml(detector.slug)}"><img src="${escapeHtml(detectionGuideDetectorIcon(detector))}" alt="">${escapeHtml(detector.label)}</a>`)
        .join("");
      return `<section><h2>${escapeHtml(family)} <span>${escapeHtml(detectors.filter((detector) => detectionGuideFamilySlug(detector.family) === familySlug).length)}</span></h2>${links}</section>`;
    }).join("");
    content.innerHTML = visibleDetectors.length
      ? visibleDetectors.map(detectionGuideArticle).join("")
      : '<article class="detection-guide-empty"><h2>No matching detections</h2><p>Try a different search term or family filter.</p></article>';
  }

  async function loadDetectionGuide() {
    const cached = getDetectionGuideCache();
    if (cached) return cached;
    let promise = getDetectionGuidePromise();
    if (!promise) {
      promise = fetch(detectionGuidePath)
        .then((response) => {
          if (!response.ok) throw new Error(`Detection guide returned ${response.status}`);
          return response.json();
        })
        .then((guide) => {
          setDetectionGuideCache(guide);
          return guide;
        })
        .finally(() => {
          setDetectionGuidePromise(null);
        });
      setDetectionGuidePromise(promise);
    }
    return promise;
  }

  async function ensureDetectionGuideRendered() {
    const content = document.querySelector("#detectionGuideContent");
    try {
      renderDetectionGuide(await loadDetectionGuide());
    } catch (error) {
      if (content) {
        content.innerHTML = `
          <article class="detection-guide-empty">
            <h2>Detection guide unavailable</h2>
            <p>${escapeHtml(error.message || "The detection guide JSON could not be loaded.")}</p>
          </article>
        `;
      }
    }
  }

  return {
    detectionGuideList,
    detectionGuideFamilySlug,
    detectionGuideFamilyIcon,
    detectionGuideDetectorIcon,
    detectionGuideSearchText,
    detectionGuideVisibleDetectors,
    detectionGuideActiveSlug,
    detectionGuideThresholdSummary,
    detectionGuideDateLabel,
    detectionGuideFamilyDisplayLabel,
    detectionGuideRenderControls,
    detectionGuideThresholdValue,
    detectionGuideThresholdLabel,
    detectionGuideThresholds,
    detectionGuideArticle,
    renderDetectionGuide,
    loadDetectionGuide,
    ensureDetectionGuideRendered,
  };
}
